import React, { useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { getRank } from '../data/initialState';

export default function ShareCard({ onClose }) {
  const { state } = useApp();
  const lang = state.language || 'en';
  const rank = getRank(state.xp || 0);
  const rankName = rank[lang] || rank.en;
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);

  const curr = lang==='en'?'$':lang==='da'?'DKK':lang==='sv'?'SEK':'kr';
  const fmt = (n) => lang==='en'?`$${Math.round(Math.abs(n)).toLocaleString()}`:`${Math.round(Math.abs(n)).toLocaleString('no')} ${curr}`;
  const fmtAmt = fmt;

  const totalSaved = (state.transactions || []).filter(t => t.cat==='Sparing').reduce((s,t) => s+Math.abs(t.amount), 0);
  const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const monthSaved = (state.transactions || []).filter(t => t.cat==='Sparing' && new Date(t.date)>=startOfMonth).reduce((s,t) => s+Math.abs(t.amount), 0);
  const startOfWeek = new Date(); startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
  const weekSaved = (state.transactions || []).filter(t => t.cat==='Sparing' && new Date(t.date)>=startOfWeek).reduce((s,t) => s+Math.abs(t.amount), 0);
  const name = state.user?.username || state.user?.name?.split(' ')[0] || 'Savely';

  const labels = {
    title: {en:'My savings journey',no:'Min sparereise',sv:'Min sparresa',da:'Min sparrejse'},
    total: {en:'Total saved',no:'Totalt spart',sv:'Totalt sparat',da:'Samlet sparet'},
    month: {en:'This month',no:'Denne måneden',sv:'Denna månad',da:'Denne måned'},
    week: {en:'This week',no:'Denne uken',sv:'Denna vecka',da:'Denne uge'},
    streak: {en:'day streak',no:'dagers streak',sv:'dagars streak',da:'dages streak'},
    rank: {en:'Rank',no:'Rang',sv:'Rang',da:'Rang'},
    join: {en:'Join me on Savely 🌱',no:'Bli med meg på Savely 🌱',sv:'Gå med mig på Savely 🌱',da:'Bliv med mig på Savely 🌱'},
    share: {en:'Share',no:'Del',sv:'Dela',da:'Del'},
    copy: {en:'Copy link',no:'Kopier lenke',sv:'Kopiera länk',da:'Kopier link'},
    copied: {en:'Copied! ✅',no:'Kopiert! ✅',sv:'Kopierat! ✅',da:'Kopieret! ✅'},
  };
  const T = (key) => labels[key]?.[lang] || labels[key]?.en || key;

  const handleShare = async () => {
    const text = `${T('join')} mysavely.app\n${T('total')}: ${fmt(totalSaved)} | ${state.streak} ${T('streak')}`;
    if (navigator.share) {
      await navigator.share({ title:'Savely', text, url:'https://mysavely.app' });
    } else {
      await navigator.clipboard.writeText('https://mysavely.app');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width:'100%', maxWidth:360 }}>
        
        {/* THE CARD */}
        <div ref={cardRef} style={{
          background:'linear-gradient(145deg, #05020F 0%, #1A0840 40%, #0D1535 70%, #050A20 100%)',
          borderRadius:24, padding:'2rem', position:'relative', overflow:'hidden',
          border:'1px solid rgba(255,255,255,0.08)',
          boxShadow:'0 32px 64px rgba(0,0,0,0.8)',
        }}>
          {/* Background glow effects */}
          <div style={{ position:'absolute', top:-60, right:-60, width:200, height:200, borderRadius:'50%', background:'radial-gradient(circle, rgba(34,201,122,0.15) 0%, transparent 70%)', pointerEvents:'none' }}/>
          <div style={{ position:'absolute', bottom:-40, left:-40, width:160, height:160, borderRadius:'50%', background:'radial-gradient(circle, rgba(0,212,255,0.1) 0%, transparent 70%)', pointerEvents:'none' }}/>

          {/* Header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
            <div>
              <div style={{ fontSize:22, fontWeight:800, color:'#fff', letterSpacing:-0.5 }}>
                Savely<span style={{ color:'#22C97A' }}>.</span>
              </div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:2 }}>mysavely.app</div>
            </div>
            <div style={{ fontSize:32 }}>🌱</div>
          </div>

          {/* User */}
          <div style={{ marginBottom:'1.5rem' }}>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.5)', marginBottom:4 }}>{T('title')}</div>
            <div style={{ fontSize:20, fontWeight:700, color:'#fff' }}>{name}</div>
            <div style={{ fontSize:12, color:'#22C97A', fontWeight:600, marginTop:2 }}>
              {rankName} · {state.streak} {T('streak')} 🔥
            </div>
          </div>

          {/* Big number */}
          <div style={{ background:'rgba(34,201,122,0.08)', border:'1px solid rgba(34,201,122,0.2)', borderRadius:16, padding:'1.25rem', marginBottom:12, textAlign:'center' }}>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', letterSpacing:2, textTransform:'uppercase', marginBottom:6 }}>{T('total')}</div>
            <div style={{ fontSize:38, fontWeight:900, color:'#22C97A', letterSpacing:-1 }}>{fmt(totalSaved)}</div>
          </div>

          {/* Month + Week */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:'1.5rem' }}>
            <div style={{ background:'rgba(255,255,255,0.04)', borderRadius:12, padding:'0.875rem', textAlign:'center' }}>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginBottom:4 }}>{T('month')}</div>
              <div style={{ fontSize:18, fontWeight:800, color:'#fff' }}>{fmt(monthSaved)}</div>
            </div>
            <div style={{ background:'rgba(255,255,255,0.04)', borderRadius:12, padding:'0.875rem', textAlign:'center' }}>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginBottom:4 }}>{T('week')}</div>
              <div style={{ fontSize:18, fontWeight:800, color:'#fff' }}>{fmt(weekSaved)}</div>
            </div>
          </div>

          {/* XP bar */}
          <div style={{ marginBottom:'1.5rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'rgba(255,255,255,0.4)', marginBottom:6 }}>
              <span>{T('rank')}: {rankName}</span>
              <span>{state.xp} XP</span>
            </div>
            <div style={{ height:6, background:'rgba(255,255,255,0.08)', borderRadius:99, overflow:'hidden' }}>
              <div style={{ width:`${Math.min(100, (state.xp % 500) / 5)}%`, height:'100%', background:'linear-gradient(90deg, #22C97A, #00D4FF)', borderRadius:99 }}/>
            </div>
          </div>

          {/* Join text */}
          <div style={{ textAlign:'center', fontSize:13, color:'rgba(255,255,255,0.5)', fontWeight:500 }}>
            {T('join')}
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display:'flex', gap:10, marginTop:16 }}>
          <button onClick={handleShare} style={{ flex:2, padding:'14px', borderRadius:14, border:'none', background:'linear-gradient(135deg,#22C97A,#16a34a)', color:'#fff', fontSize:15, fontWeight:700, cursor:'pointer' }}>
            📤 {T('share')}
          </button>
          <button onClick={async () => { await navigator.clipboard.writeText('https://mysavely.app'); setCopied(true); setTimeout(()=>setCopied(false),2000); }}
            style={{ flex:1, padding:'14px', borderRadius:14, border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.05)', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            {copied ? T('copied') : T('copy')}
          </button>
        </div>

        <button onClick={onClose} style={{ width:'100%', marginTop:10, padding:'12px', borderRadius:14, border:'none', background:'none', color:'rgba(255,255,255,0.4)', fontSize:13, cursor:'pointer' }}>
          ✕ {lang==='en'?'Close':lang==='sv'?'Stäng':lang==='da'?'Luk':'Lukk'}
        </button>
      </div>
    </div>
  );
}
